import { useMemo, useState } from 'react';
import { CityPicker, type CityValue } from '../../components/CityPicker';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { Banner, useAnnouncer } from '../../components/Status';
import { isAdmin, managesPerson } from '../../domain/permissions';
import { addDays, localDateISO, locationLabel, placeHistory, upcomingVisits, validateVisit } from '../../domain/places';
import type { Lang, PersonPlace, PersonWithNames, UUID } from '../../domain/types';
import { useI18n } from '../../i18n';
import { failureKey } from '../../lib/results';
import { uuid } from '../../lib/uuid';
import { useApp } from '../../app/AppState';
import { useRepos } from '../../repositories';

/** Short, local-language date for a YYYY-MM-DD string. */
export function formatDay(date: string, lang: Lang, withYear = false): string {
  try {
    return new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB', { day: 'numeric', month: 'short', year: withYear ? 'numeric' : undefined }).format(new Date(`${date}T12:00:00`));
  } catch {
    return date;
  }
}

/** May this viewer add or call off visits for the person? Self, helper, or administrator. */
export function useCanPlanVisits(personId: UUID): boolean {
  const { actor } = useApp();
  return !!actor && (isAdmin(actor.session.role) || managesPerson(actor, personId));
}

/**
 * Visits for one person: what is coming up (with "call it off"), and a button
 * to add one. The full record — homes and past visits — lives on the profile.
 */
export function VisitsEditor({ person, compact = false }: { person: PersonWithNames; compact?: boolean }) {
  const { t, lang } = useI18n();
  const { places, locationsById, reload } = useApp();
  const repos = useRepos();
  const { toast } = useAnnouncer();
  const canPlan = useCanPlanVisits(person.id);
  const [adding, setAdding] = useState(false);
  const today = localDateISO();
  const upcoming = useMemo(() => upcomingVisits(places, person.id, today), [places, person.id, today]);

  const callOff = async (p: PersonPlace) => {
    const r = await repos.places.cancelVisit(p.id);
    if (r.ok) {
      toast(t('places.visit.cancelled'));
      reload();
    } else toast(t(failureKey(r)));
  };

  return (
    <div className="stack">
      {!compact && <p className="small muted" style={{ margin: 0 }}>{t('places.visitsHelp')}</p>}
      {upcoming.length === 0 ? (
        <p className="small muted" style={{ margin: 0 }}>{t('places.visit.none')}</p>
      ) : (
        <ul className="list-reset stack-sm">
          {upcoming.map((p) => {
            const active = p.from <= today;
            return (
              <li key={p.id} className="row-nowrap card-soft visit-row">
                <span className={`visit-mark ${active ? 'is-now' : ''}`} aria-hidden="true"><Icon name="suitcase" /></span>
                <span className="grow">
                  <span className="bold">{locationLabel(locationsById.get(p.locationId), lang)}</span>
                  <span className="small muted block">
                    {t('places.record.range', { from: formatDay(p.from, lang), to: formatDay(p.to ?? p.from, lang) })}
                    {' · '}
                    {active ? t('places.visit.now') : t('places.visit.upcoming')}
                    {p.note ? ` · ${p.note}` : ''}
                  </span>
                </span>
                {canPlan && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => void callOff(p)}>
                    {t('places.visit.cancel')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canPlan && (
        <button type="button" className="btn btn-secondary" onClick={() => setAdding(true)}>
          <Icon name="suitcase" />
          <span>{t('places.addVisit')}</span>
        </button>
      )}
      <AddVisitDialog open={adding} onClose={() => setAdding(false)} person={person} />
    </div>
  );
}

function AddVisitDialog({ open, onClose, person }: { open: boolean; onClose: () => void; person: PersonWithNames }) {
  const { t, lang } = useI18n();
  const repos = useRepos();
  const { reload } = useApp();
  const { toast } = useAnnouncer();
  const today = localDateISO();
  const [where, setWhere] = useState<CityValue>(null);
  const [from, setFrom] = useState(addDays(today, 1));
  const [to, setTo] = useState(addDays(today, 8));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setWhere(null);
    setFrom(addDays(today, 1));
    setTo(addDays(today, 8));
    setNote('');
    setError(null);
  };

  const check = validateVisit(from, to, today);
  const canSave = !!where && check === 'ok' && !busy;

  const save = async () => {
    if (!where) return;
    setBusy(true);
    setError(null);
    const location = where.kind === 'location' ? where.location : await repos.places.ensureCityLocation(where.city);
    const r = await repos.places.addVisit({ personId: person.id, locationId: location.id, from, to, note, idempotencyKey: uuid() });
    setBusy(false);
    if (r.ok) {
      toast(t('places.visit.saved'));
      reload();
      reset();
      onClose();
    } else setError(t(failureKey(r)));
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t('places.addVisit')}
      actions={
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!canSave}>
          <Icon name="check" />
          <span>{t('places.visit.save')}</span>
        </button>
      }
    >
      <div className="stack">
        {error && <Banner kind="error">{error}</Banner>}
        <CityPicker label={t('places.visit.where')} value={where} onChange={setWhere} autoFocus />
        <div className="input-row">
          <div className="field grow">
            <label className="field-label" htmlFor="visit-from">{t('places.visit.from')}</label>
            <input id="visit-from" className="input" type="date" value={from} min={addDays(today, -365)} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field grow">
            <label className="field-label" htmlFor="visit-to">{t('places.visit.to')}</label>
            <input id="visit-to" className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        {check !== 'ok' && from && to ? <p className="small" style={{ color: 'var(--danger)', margin: 0 }}>{t(`places.${check}` as 'places.invalidDate')}</p> : null}
        <div className="field">
          <label className="field-label" htmlFor="visit-note">{t('places.visit.note')}</label>
          <input id="visit-note" className="input" value={note} placeholder={t('places.visit.noteHint')} onChange={(e) => setNote(e.target.value)} maxLength={80} />
        </div>
        <p className="small muted" style={{ margin: 0 }}>{lang === 'he' ? t('places.visitsHelp') : t('places.visitsHelp')}</p>
      </div>
    </Dialog>
  );
}

/** Every home and visit for a person, newest first — the travel record. */
export function PlaceRecord({ person }: { person: PersonWithNames }) {
  const { t, lang } = useI18n();
  const { places, locationsById } = useApp();
  const rows = useMemo(() => placeHistory(places, person.id), [places, person.id]);
  if (!rows.length) return null;
  return (
    <ul className="list-reset stack-sm">
      {rows.map((p) => (
        <li key={p.id} className={`row-nowrap place-row ${p.cancelled ? 'is-cancelled' : ''}`}>
          <span className="place-kind" aria-hidden="true"><Icon name={p.kind === 'home' ? 'home' : 'suitcase'} /></span>
          <span className="grow">
            <span className="bold">{locationLabel(locationsById.get(p.locationId), lang)}</span>
            <span className="small muted block">
              {p.kind === 'home' ? t('places.home') : t('places.visits')}
              {' · '}
              {p.to ? t('places.record.range', { from: formatDay(p.from, lang, true), to: formatDay(p.to, lang, true) }) : t('places.record.since', { date: formatDay(p.from, lang, true) })}
              {p.cancelled ? ` · ${t('places.visit.cancelledMark')}` : ''}
              {p.note ? ` · ${p.note}` : ''}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
